'use client';

import React, { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Section } from '@/components/ui/Section';
import { Button } from '@/components/ui/Button';
import styles from './KioskShowcase.module.css';

export interface KioskShowcaseProps {
  title: string;
  subtitle: string;
  altTexts?: {
    blue?: string;
    orange?: string;
    pink?: string;
    black?: string;
  };
  ctaText?: string;
  ctaUrl?: string;
  ctaId?: string;
  ctaPosition?: string;
}

/**
 * KioskShowcase Component
 * Displays all 4 kiosk variants with floating animation effect
 * On mobile: auto-playing slider with touch support
 */
export function KioskShowcase({ 
  title, 
  subtitle, 
  altTexts,
  ctaText,
  ctaUrl,
  ctaId,
  ctaPosition
}: KioskShowcaseProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-play slider on mobile
  useEffect(() => {
    if (!isMobile) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 4);
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [isMobile]);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
          }
        });
      },
      { threshold: 0.1 }
    );

    const kiosks = sectionRef.current?.querySelectorAll(`.${styles.kiosk}`);
    kiosks?.forEach((kiosk) => observer.observe(kiosk));

    return () => observer.disconnect();
  }, []);

  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50; // Minimum swipe distance

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swiped left - next slide
        setCurrentSlide((prev) => (prev + 1) % 4);
      } else {
        // Swiped right - previous slide
        setCurrentSlide((prev) => (prev - 1 + 4) % 4);
      }
    }

    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  const kiosks = [
    {
      id: 'blue',
      src: '/images/product-variants/kiosk-blue.webp',
      alt: altTexts?.blue || 'Jamezz kiosk in blue - Self-ordering kiosk for hospitality with touchscreen POS integration',
      color: '#4A90E2',
    },
    {
      id: 'orange',
      src: '/images/product-variants/kiosk-orange.webp',
      alt: altTexts?.orange || 'Jamezz kiosk in orange - Self-service terminal for restaurants with contactless ordering',
      color: '#F5A623',
    },
    {
      id: 'pink',
      src: '/images/product-variants/kiosk-pink.webp',
      alt: altTexts?.pink || 'Jamezz kiosk in pink - Modern ordering kiosk for hospitality with QR scan and menu display',
      color: '#E91E63',
    },
    {
      id: 'black',
      src: '/images/product-variants/kiosk-zwart.webp',
      alt: altTexts?.black || 'Jamezz kiosk in black - Premium self-ordering system for restaurants and hotels',
      color: '#2C3E50',
    },
  ];

  return (
    <Section background="primary" paddingY="none">
      <div className={styles.showcase} ref={sectionRef}>
      <div className={styles.background}>
        <div className={styles.gradientOrb1} />
        <div className={styles.gradientOrb2} />
        <div className={styles.grid} />
      </div>

      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        <div 
          className={styles.kiosksGrid}
          ref={sliderRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={isMobile ? {
            transform: `translateX(-${currentSlide * 100}%)`,
            transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          } : {}}
        >
          {kiosks.map((kiosk, index) => (
            <div
              key={kiosk.id}
              className={styles.kiosk}
              style={{
                animationDelay: `${index * 0.15}s`,
              }}
            >
              <div className={styles.kioskInner}>
                <div
                  className={styles.kioskGlow}
                  style={{
                    background: `radial-gradient(circle at center, ${kiosk.color}40 0%, transparent 70%)`,
                  }}
                />
                <div className={styles.imageWrapper}>
                  <Image
                    src={kiosk.src}
                    alt={kiosk.alt}
                    width={400}
                    height={800}
                    className={styles.kioskImage}
                    loading={index < 2 ? 'eager' : 'lazy'}
                    
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation dots - only on mobile */}
        {isMobile && (
          <div className={styles.sliderDots}>
            {kiosks.map((_, index) => (
              <button
                key={index}
                className={`${styles.dot} ${index === currentSlide ? styles.dotActive : ''}`}
                onClick={() => setCurrentSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
        
        {/* CTA Button */}
        {ctaText && ctaUrl && (
          <div className={styles.ctaWrapper}>
            <Link href={ctaUrl}>
              <Button
                variant="primary"
                size="lg"
                data-cta-id={ctaId}
                data-cta-position={ctaPosition}
                data-cta-variant="primary"
              >
                {ctaText}
              </Button>
            </Link>
          </div>
        )}
      </div>
      </div>
    </Section>
  );
}

